import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { Check, AlertTriangle, Copy, Send, Clock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import ScreenHeader from "../components/ScreenHeader";
import PartnerBadge from "../components/PartnerBadge";

export const TransactionDetailScreen = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { transactions } = useApp();
  const tx = transactions.find((x) => x.id === id);
  const [copied, setCopied] = useState(false);

  if (!tx) {
    return (
      <div className="h-full flex flex-col">
        <ScreenHeader title="Transaction" />
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          Transaction not found.
        </div>
      </div>
    );
  }

  const copyRef = () => {
    navigator.clipboard?.writeText(tx.reference);
    toast.success("Reference copied to clipboard");
  };

  const handleSendReceipt = () => {
    toast.success("Receipt sent to your email");
  };

  const isFailed = tx.status === "FAILED";

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      className="h-full flex flex-col pb-6"
      data-testid="transaction-detail-screen"
    >
      <ScreenHeader title="Transaction" testId="detail-back-btn" onBack={() => nav(-1)} />

      <div className="px-6 pt-2 text-center">
        <div
          className={
            "mx-auto w-16 h-16 rounded-full flex items-center justify-center " +
            (isFailed ? "bg-red-50 border border-red-100" : "bg-emerald-500")
          }
        >
          {isFailed ? (
            <AlertTriangle className="w-8 h-8 text-red-500" strokeWidth={2.2} />
          ) : (
            <Check className="w-8 h-8 text-white" strokeWidth={3} />
          )}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mt-4">
          {isFailed ? "Failed" : "Completed"}
        </div>
        <div className="font-display text-[34px] font-bold mt-1 tracking-tight">
          {tx.amount.toFixed(2)} kr
        </div>
        <div className="text-[12px] text-gray-500 mt-1">
          {new Date(tx.createdAt).toLocaleString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      <div className="px-6 mt-5">
        <PartnerBadge partner={tx.partner} variant="banner" />
      </div>

      <div className="px-6 mt-5">
        <div className="rounded-3xl border border-gray-100 bg-gray-50 p-5 space-y-3 text-[13px]">
          <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
            <Avatar initials={tx.recipient.initials} color={tx.recipient.color} size={40} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                Recipient
              </div>
              <div className="font-semibold text-gray-900 truncate">
                {tx.recipient.name}
              </div>
              <div className="text-[11px] text-gray-500 truncate">{tx.recipient.phone}</div>
            </div>
          </div>
          <Row label="Recipient gets" value={`${tx.recipientGets.toFixed(2)} kr`} />
          <Row label="Fees" value={`${tx.fee.toFixed(2)} kr`} />
          <Row label="Payment method" value={tx.method} />
          <Row label="Executed by" value={tx.partner} />
          <Row
            label="Reference"
            value={
              <button
                onClick={copyRef}
                data-testid="detail-copy-reference"
                className="inline-flex items-center gap-1.5 font-mono text-[12px] text-gray-900 hover:text-gray-700"
              >
                {tx.reference}
                <Copy className="w-3 h-3" strokeWidth={2.2} />
              </button>
            }
          />
          <Row
            label="Status"
            value={
              <span
                className={
                  "text-[11px] font-bold px-2 py-0.5 rounded-full " +
                  (isFailed
                    ? "text-red-600 bg-red-50"
                    : "text-emerald-700 bg-emerald-50")
                }
              >
                {tx.status}
              </span>
            }
          />
        </div>
      </div>

      {copied && (
        <div className="text-center text-[11px] text-emerald-600 font-semibold mt-2">
          Reference copied
        </div>
      )}

      <div className="mt-auto px-6 pt-6 space-y-2">
        {!isFailed && (
          <button
            data-testid="detail-send-receipt-btn"
            onClick={handleSendReceipt}
            className="w-full bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white h-12 rounded-full font-semibold text-[14px] hover:bg-gray-200 dark:hover:bg-white/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" strokeWidth={2.2} />
            Send receipt
          </button>
        )}
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-500 pt-1">
          <Clock className="w-3 h-3" strokeWidth={2.5} />
          Shwiip acts as a technical interface only.
        </div>
      </div>
    </motion.div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-semibold text-gray-900">{value}</span>
  </div>
);

export default TransactionDetailScreen;
